/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Events/AdminEventsManager.tsx.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosClient from "../../../../api/axiosClient";
import OutlineButton from "../../../../components/common/OutlineButton";
import { adminEventService, type AdminEvent } from "../../../../services/adminEventService";
import { resolveUploadAssetUrl } from "../../../../utils/resolveUploadAssetUrl";
import { Pencil, Pause, Play, Trash2 } from "lucide-react";
import "../Admin.css";
import "./AdminEventsManager.css";

type EventFormState = {
  title: string;
  content: string;
  eventDate: string; // datetime-local value
  place: string;
  image: string;
  registrationLink: string;
};

const emptyForm: EventFormState = {
  title: "",
  content: "",
  eventDate: "",
  place: "",
  image: "",
  registrationLink: "",
};

const toDateTimeLocalValue = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** React component — Admin Events Manager. */
export default function AdminEventsManager() {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  // Show paused events by default so status changes remain visible.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selected, setSelected] = useState<AdminEvent | null>(null);
  const [form, setForm] = useState<EventFormState>(emptyForm);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [pickedImage, setPickedImage] = useState<File | null>(null);
  const [pickedImagePreview, setPickedImagePreview] = useState<string>("");
  const [imageUploadError, setImageUploadError] = useState<string | null>(null); // server-side upload error on save

  const load = useCallback(async (opts?: { includeInactive?: boolean }) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await adminEventService.list(opts?.includeInactive ?? includeInactive);
      const list = res.data?.data || [];
      setEvents(list);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load events.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => {
    const list = [...events];
    list.sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
    return list;
  }, [events]);

  const onSelect = (ev: AdminEvent) => {
    setSelected(ev);
    setForm({
      title: ev.title || "",
      content: ev.content || "",
      eventDate: toDateTimeLocalValue(ev.eventDate),
      place: ev.place || "",
      image: ev.image || "",
      registrationLink: ev.registrationLink || "",
    });
    if (pickedImagePreview) URL.revokeObjectURL(pickedImagePreview);
    if (imageInputRef.current) imageInputRef.current.value = "";
    setPickedImage(null);
    setPickedImagePreview("");
    setImageUploadError(null);
    setSuccess(null);
    setError(null);
    setIsModalOpen(true);
  };

  const onNew = () => {
    setSelected(null);
    setForm(emptyForm);
    if (pickedImagePreview) URL.revokeObjectURL(pickedImagePreview);
    if (imageInputRef.current) imageInputRef.current.value = "";
    setPickedImage(null);
    setPickedImagePreview("");
    setImageUploadError(null);
    setSuccess(null);
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const canSave = useMemo(() => {
    if (loading || saving) return false;
    if (!form.title.trim()) return false;
    if (!form.content.trim()) return false;
    if (!form.eventDate) return false;
    if (!form.place.trim()) return false;
    return true;
  }, [form, loading, saving]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setImageUploadError(null);
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("content", form.content.trim());
      fd.append("eventDate", new Date(form.eventDate).toISOString());
      fd.append("place", form.place.trim());
      if (form.registrationLink.trim()) {
        fd.append("registrationLink", form.registrationLink.trim());
      }
      // If a file is selected, send it as "image" (multipart upload).
      if (pickedImage) {
        fd.append("image", pickedImage);
      }

      if (selected) {
        await adminEventService.updateMultipart(selected.id, fd);
        setSuccess("Event updated.");
      } else {
        await adminEventService.createMultipart(fd);
        setSuccess("Event created.");
      }
      await load();
      setIsModalOpen(false);
      // reset form after save
      if (pickedImagePreview) URL.revokeObjectURL(pickedImagePreview);
      if (imageInputRef.current) imageInputRef.current.value = "";
      setPickedImage(null);
      setPickedImagePreview("");
      setImageUploadError(null);
      setSelected(null);
      setForm(emptyForm);
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || "Failed to save event.";
      // Upload errors are expected to be 400; show them near the image field too.
      if (String(message).toLowerCase().includes("image") || e?.response?.status === 400) {
        setImageUploadError(message);
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (ev: AdminEvent) => {
    setError(null);
    setSuccess(null);
    try {
      const fd = new FormData();
      const nextIsActive = !ev.isActive;
      fd.append("isActive", String(nextIsActive));
      await adminEventService.updateMultipart(ev.id, fd);
      setSuccess(ev.isActive ? "Event paused." : "Event activated.");

      // If user pauses an event while paused events are hidden, auto-show them
      // so the status change is immediately reflected in the list.
      if (!nextIsActive && !includeInactive) {
        setIncludeInactive(true);
        await load({ includeInactive: true });
      } else {
        await load();
      }

      if (selected?.id === ev.id) {
        setSelected(null);
        setForm(emptyForm);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to update event status.");
    }
  };

  const handleDelete = async (ev: AdminEvent) => {
    const ok = window.confirm(`Delete event "${ev.title}"? This cannot be undone.`);
    if (!ok) return;
    setError(null);
    setSuccess(null);
    try {
      await adminEventService.remove(ev.id);
      setSuccess("Event deleted.");
      await load();
      if (selected?.id === ev.id) onNew();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to delete event.");
    }
  };

  const previewUrl = form.image ? resolveUploadAssetUrl(form.image) : "";
  const effectivePreviewUrl = pickedImagePreview || previewUrl;

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Events</span>
      </div>

      <div className="admin-form-root">
        <div className="admin-events-wrapper dataset-container">
      <div className="dataset-section">
        <h3 className="dataset-section-title">Event Management</h3>
      <div className="admin-events-toolbar">
        <label className="admin-events-checkbox">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            disabled={loading || saving}
          />
          Show paused events
        </label>
        <div className="admin-events-actions">
          <OutlineButton label="New event" onClick={onNew} type="button" />
          <OutlineButton
            label={loading ? "Refreshing..." : "Refresh"}
            onClick={() => void load()}
            type="button"
            disabled={loading}
          />
        </div>
      </div>

      <div className="admin-events-grid">
        <section className="admin-events-list">
          {loading ? (
            <div className="admin-events-hint">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="admin-events-hint">No events found.</div>
          ) : (
            <table className="admin-events-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Place</th>
                  <th>Status</th>
                  <th style={{ width: 280 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((ev) => (
                  <tr key={ev.id} className={selected?.id === ev.id ? "is-selected" : ""}>
                    <td>
                      <div className="admin-events-title">{ev.title}</div>
                    </td>
                    <td>{new Date(ev.eventDate).toLocaleString()}</td>
                    <td>{ev.place}</td>
                    <td>
                      <span className={`admin-events-status ${ev.isActive ? "active" : "paused"}`}>
                        {ev.isActive ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-events-row-actions">
                        <button
                          type="button"
                          className="admin-events-icon-btn"
                          onClick={() => onSelect(ev)}
                          aria-label="Edit event"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>

                        <button
                          type="button"
                          className="admin-events-icon-btn admin-events-icon-btn--warn"
                          onClick={() => void handleToggleActive(ev)}
                          aria-label={ev.isActive ? "Pause event" : "Activate event"}
                          title={ev.isActive ? "Pause" : "Activate"}
                        >
                          {ev.isActive ? <Pause size={18} /> : <Play size={18} />}
                        </button>

                        <button
                          type="button"
                          className="admin-events-icon-btn admin-events-icon-btn--danger"
                          onClick={() => void handleDelete(ev)}
                          aria-label="Delete event"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
        </div>
      </div>
      </div>

      {isModalOpen &&
        createPortal(
          <div
            className="admin-events-modal-overlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              // close when clicking the shaded backdrop
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div className="admin-events-modal">
            <div className="admin-events-modal-head">
              <div className="admin-events-modal-title">
                {selected ? "Edit event" : "Create event"}
              </div>
              <button
                type="button"
                className="admin-events-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="admin-events-modal-body">
              <div className="dataset-grid">
                <div className="form-field admin-events-field">
                  <label>Title</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Event title"
                    disabled={saving}
                  />
                </div>
                <div className="form-field admin-events-field" style={{ gridColumn: "1 / -1" }}>
                  <label>Content (one paragraph)</label>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                    placeholder="One paragraph description…"
                    rows={4}
                    disabled={saving}
                    style={{ padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, width: "100%", boxSizing: "border-box" }}
                  />
                </div>
                <div className="form-field admin-events-field">
                  <label>Event date/time</label>
                  <input
                    type="datetime-local"
                    value={form.eventDate}
                    onChange={(e) => setForm((p) => ({ ...p, eventDate: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div className="form-field admin-events-field">
                  <label>Place</label>
                  <input
                    value={form.place}
                    onChange={(e) => setForm((p) => ({ ...p, place: e.target.value }))}
                    placeholder="Venue name / address"
                    disabled={saving}
                  />
                </div>
                <div className="form-field admin-events-field" style={{ gridColumn: "1 / -1" }}>
                <label>Upload image (optional)</label>
                <div className="admin-events-upload-row">
                  <input
                    ref={imageInputRef}
                    className="admin-events-file-input"
                    type="file"
                    accept="image/*"
                    disabled={saving}
                    onChange={(e) => {
                      setImageUploadError(null);
                      const file = e.target.files?.[0] || null;
                      if (pickedImagePreview) URL.revokeObjectURL(pickedImagePreview);
                      setPickedImage(file);
                      setPickedImagePreview(file ? URL.createObjectURL(file) : "");
                    }}
                  />
                  {pickedImage && (
                    <button
                      type="button"
                      className="admin-events-clear-file"
                      onClick={() => {
                        if (pickedImagePreview) URL.revokeObjectURL(pickedImagePreview);
                        if (imageInputRef.current) imageInputRef.current.value = "";
                        setPickedImage(null);
                        setPickedImagePreview("");
                        setImageUploadError(null);
                      }}
                      disabled={saving}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {imageUploadError && <div className="admin-events-error">{imageUploadError}</div>}

                {effectivePreviewUrl && (
                  <div className="admin-events-image-preview">
                    <img src={effectivePreviewUrl} alt="Event preview" />
                  </div>
                )}
                </div>
                <div className="form-field admin-events-field">
                  <label>Registration link</label>
                <input
                  value={form.registrationLink}
                  onChange={(e) => setForm((p) => ({ ...p, registrationLink: e.target.value }))}
                  placeholder="https://…"
                  disabled={saving}
                />
                </div>
              </div>

              {error && <div className="admin-events-error">{error}</div>}
              {success && <div className="admin-events-success">{success}</div>}
              <div className="admin-events-hint">
                Pause hides the event from the public Events page. Delete permanently removes it.
              </div>
            </div>

            <div className="admin-events-modal-foot">
              {selected && (
                <OutlineButton
                  label={selected.isActive ? "Pause" : "Activate"}
                  onClick={() => void handleToggleActive(selected)}
                  type="button"
                />
              )}
              {selected && (
                <OutlineButton
                  label="Delete"
                  onClick={() => void handleDelete(selected)}
                  type="button"
                />
              )}
              <div className="admin-events-modal-foot-spacer" />
              <OutlineButton
                label={saving ? "Saving..." : selected ? "Update event" : "Create event"}
                onClick={handleSave}
                type="button"
                disabled={!canSave}
              />
            </div>
          </div>
          </div>,
          document.body
        )}
    </div>
  );
}

